import { invoke } from "@tauri-apps/api/core";

/** macOS x-apple.systempreferences URLs for jumping straight to a privacy pane.
 *  The URL fragments are private, but the Privacy pane ids have stayed stable
 *  across System Preferences (macOS 12 and older), System Settings (13+), and
 *  the Sequoia/Tahoe rename from Screen Recording to Screen & System Audio
 *  Recording. Keep OS buckets explicit so future pane changes are local here. */
export type Permission = "accessibility" | "microphone" | "screen_recording";

/** Superset of Permission — privacy panes the user might want to jump
 *  to outside the cursor-led grant flow (e.g. via the tray). Mirrors
 *  the rows in System Settings → Privacy & Security. */
export type PrivacyPane =
  | Permission
  | "speech_recognition"
  | "input_monitoring"
  | "files_folders"
  | "full_disk_access";

type MacOSUrlBucket =
  | "monterey_or_older"
  | "ventura_to_sonoma"
  | "sequoia_or_newer";

const PRIVACY_PANE = "x-apple.systempreferences:com.apple.preference.security";
let restoreOverlayOnFocusAttached = false;

function detachOverlayRestoreListeners(): void {
  if (typeof window === "undefined" || !restoreOverlayOnFocusAttached) return;
  restoreOverlayOnFocusAttached = false;
  window.removeEventListener("focus", restoreOverlayAfterSystemUi);
  window.removeEventListener("pageshow", restoreOverlayAfterSystemUi);
  window.removeEventListener("pagehide", restoreOverlayAfterSystemUi);
  document.removeEventListener("visibilitychange", restoreOverlayWhenVisible);
}

function restoreOverlayAfterSystemUi(): void {
  void restoreOverlayLevel();
}

function restoreOverlayWhenVisible(): void {
  if (document.visibilityState === "visible") restoreOverlayAfterSystemUi();
}

export async function restoreOverlayLevel(): Promise<void> {
  detachOverlayRestoreListeners();
  try {
    await invoke("restore_overlay_level");
  } catch {
    // The webview may already be unloading. Restoration is best-effort here;
    // every remaining display receives the same native command independently.
  }
}

const URLS: Record<MacOSUrlBucket, Record<PrivacyPane, string>> = {
  monterey_or_older: {
    accessibility: `${PRIVACY_PANE}?Privacy_Accessibility`,
    microphone: `${PRIVACY_PANE}?Privacy_Microphone`,
    screen_recording: `${PRIVACY_PANE}?Privacy_ScreenCapture`,
    speech_recognition: `${PRIVACY_PANE}?Privacy_SpeechRecognition`,
    input_monitoring: `${PRIVACY_PANE}?Privacy_ListenEvent`,
    files_folders: `${PRIVACY_PANE}?Privacy_Files_Folders`,
    full_disk_access: `${PRIVACY_PANE}?Privacy_AllFiles`,
  },
  ventura_to_sonoma: {
    accessibility: `${PRIVACY_PANE}?Privacy_Accessibility`,
    microphone: `${PRIVACY_PANE}?Privacy_Microphone`,
    screen_recording: `${PRIVACY_PANE}?Privacy_ScreenCapture`,
    speech_recognition: `${PRIVACY_PANE}?Privacy_SpeechRecognition`,
    input_monitoring: `${PRIVACY_PANE}?Privacy_ListenEvent`,
    files_folders: `${PRIVACY_PANE}?Privacy_Files_Folders`,
    full_disk_access: `${PRIVACY_PANE}?Privacy_AllFiles`,
  },
  sequoia_or_newer: {
    accessibility: `${PRIVACY_PANE}?Privacy_Accessibility`,
    microphone: `${PRIVACY_PANE}?Privacy_Microphone`,
    screen_recording: `${PRIVACY_PANE}?Privacy_ScreenCapture`,
    speech_recognition: `${PRIVACY_PANE}?Privacy_SpeechRecognition`,
    input_monitoring: `${PRIVACY_PANE}?Privacy_ListenEvent`,
    files_folders: `${PRIVACY_PANE}?Privacy_Files_Folders`,
    full_disk_access: `${PRIVACY_PANE}?Privacy_AllFiles`,
  },
};

export function macOSUrlBucket(
  majorVersion: number | null | undefined,
): MacOSUrlBucket {
  if (majorVersion === undefined || majorVersion === null)
    return "sequoia_or_newer";
  if (majorVersion >= 15) return "sequoia_or_newer";
  if (majorVersion >= 13) return "ventura_to_sonoma";
  return "monterey_or_older";
}

export function getDeepLink(
  p: PrivacyPane,
  majorVersion?: number | null,
): string {
  return URLS[macOSUrlBucket(majorVersion)][p];
}

export async function getDeepLinkForCurrentMacOS(
  p: PrivacyPane,
): Promise<string> {
  try {
    const major = await invoke<number | null>("get_macos_major_version");
    return getDeepLink(p, major);
  } catch {
    return getDeepLink(p);
  }
}

export async function lowerOverlayUntilWindowFocus(): Promise<void> {
  await invoke("lower_overlay_for_system_dialog").catch(() => {});
  if (typeof window === "undefined" || restoreOverlayOnFocusAttached) return;

  restoreOverlayOnFocusAttached = true;
  window.addEventListener("focus", restoreOverlayAfterSystemUi);
  window.addEventListener("pageshow", restoreOverlayAfterSystemUi);
  window.addEventListener("pagehide", restoreOverlayAfterSystemUi);
  document.addEventListener("visibilitychange", restoreOverlayWhenVisible);
}

async function openSystemSettingsUrl(url: string): Promise<void> {
  try {
    await invoke("open_privacy_pane", { url });
  } catch {
    try {
      const { openUrl } = await import("@tauri-apps/plugin-opener");
      await openUrl(url);
    } catch (error) {
      await restoreOverlayLevel();
      throw error;
    }
  }
}

export async function openPrivacyPaneForCurrentMacOS(
  p: PrivacyPane,
  options: { lowerOverlay?: boolean } = {},
): Promise<string> {
  const url = await getDeepLinkForCurrentMacOS(p);
  if (options.lowerOverlay !== false) {
    await lowerOverlayUntilWindowFocus();
  }
  await openSystemSettingsUrl(url);
  return url;
}

/** Open System Settings → Accessibility → Spoken Content, where users download
 *  enhanced/premium system voices via "Manage Voices…". macOS has NO URL scheme
 *  to deep-link a specific voice's download, so the Spoken Content pane is the
 *  closest we can get. The `?SpokenContent` anchor on the Universal Access pane
 *  works across Monterey→Sequoia; reuses `open_privacy_pane`, which hands the
 *  URL to LaunchServices. */
export async function openSpokenContentSettings(): Promise<void> {
  const url =
    "x-apple.systempreferences:com.apple.preference.universalaccess?SpokenContent";
  await lowerOverlayUntilWindowFocus();
  await openSystemSettingsUrl(url);
}

/** Open System Settings → Keyboard → Dictation. This is the master Dictation
 *  toggle the OS gates `SFSpeechRecognizer` behind — distinct from the
 *  Privacy & Security → Speech Recognition pane (`speech_recognition`), which
 *  only governs per-app authorization. When Dictation is off, the recognizer
 *  reports available but the recognition task fails at runtime, so this is the
 *  pane to send users to. The `com.apple.Keyboard-Settings.extension` pane id
 *  is the System Settings Keyboard pane on macOS 13+ (which contains the
 *  Dictation toggle; macOS exposes no deeper anchor to the row itself). Reuses
 *  `open_privacy_pane`, falling back to the URL opener if that command fails. */
export async function openDictationSettings(): Promise<void> {
  const url = "x-apple.systempreferences:com.apple.Keyboard-Settings.extension";
  await lowerOverlayUntilWindowFocus();
  await openSystemSettingsUrl(url);
}
